import React from 'react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from './ui/accordion';

/**
 * Presentational FAQ section.
 *
 * Props:
 *   eyebrow, title, subtitle — section header copy
 *   items — array of { q, a }
 *   accent — tailwind color class used for the eyebrow pill (defaults to brand gold)
 *   testIdPrefix — scopes data-testid per-page (e.g., "systems-faq" / "academy-faq")
 */
export default function FAQSection({
  eyebrow = 'FAQ',
  title,
  subtitle,
  items = [],
  accent = 'text-[#f5a623]',
  testIdPrefix = 'faq',
  aside = null,
}) {
  if (!items.length) return null;

  return (
    <section
      id="faq"
      data-testid={`${testIdPrefix}-section`}
      className="relative border-t border-gray-100 bg-[#fafafa] py-20"
    >
      <div className="zh-container grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] gap-10 lg:gap-16">
        <header className="lg:sticky lg:top-24 lg:self-start">
          <span className={`inline-block text-[11px] font-extrabold uppercase tracking-[0.2em] ${accent}`}>
            {eyebrow}
          </span>
          <h2 className="mt-3 text-4xl sm:text-5xl font-extrabold tracking-tight text-gray-900 leading-[1.05]">
            {title}
          </h2>
          {subtitle && (
            <p className="mt-5 text-[17px] text-gray-600 leading-relaxed max-w-lg">
              {subtitle}
            </p>
          )}
          {aside && <div className="mt-7 max-w-md">{aside}</div>}
        </header>

        <Accordion
          type="single"
          collapsible
          defaultValue="faq-0"
          className="rounded-2xl border border-gray-200 bg-white shadow-[0_4px_24px_-8px_rgba(0,0,0,0.08)] divide-y divide-gray-100"
        >
          {items.map((item, i) => (
            <AccordionItem
              key={i}
              value={`faq-${i}`}
              className="px-6 first:rounded-t-2xl last:rounded-b-2xl border-0"
              data-testid={`${testIdPrefix}-item-${i}`}
            >
              <AccordionTrigger
                className="text-left text-[17px] font-semibold text-gray-900 hover:no-underline py-5"
                data-testid={`${testIdPrefix}-trigger-${i}`}
              >
                {item.q}
              </AccordionTrigger>
              <AccordionContent className="text-[15px] text-gray-600 leading-relaxed pb-5">
                {item.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}
